import { MarkdownContent } from "@/app/components/Markdown";
import SourceCarousel from "@/app/components/SourceCarousel";
import FeedbackThumbs from "@/app/components/feedbackThumbs";
import { Themes, getTheme } from "@/app/components/header/ThemeSelector";
import { CpuChipIcon, UserCircleIcon } from "@heroicons/react/16/solid";
import { ChatMessageDTO } from "@onetutor/shared/dto";
import { MessageEnum } from "@onetutor/shared/type";
import { useParams } from "react-router";

interface ChatBubbleProps {
  message: ChatMessageDTO;
  messageArray: ChatMessageDTO[];
  citations: string[];
}

export function ChatBubble({
  message,
  messageArray,
  citations,
}: ChatBubbleProps) {
  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("Course ID is required");
  }

  const isUser = message.author === MessageEnum.HUMAN;
  const latestMsg = messageArray[messageArray.length - 1];
  const isLatestAI =
    latestMsg.author === MessageEnum.AI && latestMsg.id === message.id;
  const isFirst = messageArray[0].id === message.id;

  const Icon = isUser ? UserCircleIcon : CpuChipIcon;

  return (
    <div
      key={`KEY-${message.id}`}
      className={`chat ${isUser ? "chat-end" : "chat-start p-0"}`}
    >
      <div className="chat-image avatar">
        <div className="hidden sm:w-10">
          <Icon className="hidden sm:block" />
        </div>
      </div>
      <div
        className={`chat-bubble prose-markdown my-1 w-auto break-words rounded-xl py-[1px] text-[14px] shadow-sm sm:text-[16px] ${isUser ? "chat-bubble-primary text-primary-content" : "chat-bubble-neutral"}`}
      >
        <MarkdownContent
          components={{
            table: ({ node, ...props }) => (
              <div className="table-container">
                <table {...props} />
              </div>
            ),
          }}
        >
          {message.content}
        </MarkdownContent>

        {isLatestAI && citations.length > 0 && (
          <SourceCarousel courseId={courseId} citations={citations} />
        )}

        {!isUser && !isFirst && isLatestAI && (
          <div className="-bottom-9 absolute left-0 group-hover:opacity-100">
            <FeedbackThumbs
              feedbackType="message"
              courseId={courseId}
              id={message.id}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface ChatBubbleStreamProps {
  lastMessage: string;
  isStreaming: boolean;
  setCitations: (array: string[]) => void;
}

export function ChatBubbleStream({
  lastMessage,
  isStreaming,
}: ChatBubbleStreamProps) {
  const streamCursor = isStreaming
    ? getTheme() === Themes.light
      ? " ⚫"
      : " ⚪️"
    : "";

  return (
    <div className="chat chat-start p-0">
      <div className="chat-image avatar">
        <div className="hidden sm:w-10">
          <CpuChipIcon className="hidden sm:block" />
        </div>
      </div>
      <div className="chat-bubble chat-bubble-neutral prose-markdown my-1 w-auto break-words rounded-xl py-[1px] text-[14px] shadow-sm sm:text-[16px]">
        <MarkdownContent
          components={{
            table: ({ node, ...props }) => (
              <div className="table-container">
                <table {...props} />
              </div>
            ),
          }}
        >
          {`${lastMessage}${streamCursor}`}
        </MarkdownContent>
      </div>
    </div>
  );
}
